import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFarmStore } from '@/store/useFarmStore';
import { Navbar } from '@/components/layout/Navbar';
import { WizardStep } from '@/components/farm/WizardStep';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Bird, Fish, Trees, UserCircle, Rabbit, MapPin, ShieldCheck, GraduationCap, DollarSign, Square, Clock, Droplets, Zap, Camera, Landmark, Upload } from 'lucide-react';

const GH_REGIONS = ['Greater Accra', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern', 'Northern', 'Oti', 'Upper East', 'Upper West', 'Volta', 'Western'] as const;
const NG_STATES = ['Lagos', 'Kano', 'Rivers', 'Oyo', 'Delta', 'Edo', 'Imo', 'Kaduna', 'Enugu', 'Abia'] as const;
import { generateRecommendations } from '@/lib/recommendation-engine';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import type { FarmingPackage } from '@shared/types';
import { useCurrency } from '@/hooks/use-currency';
import { convertFromBaseCurrency, convertToBaseCurrency } from '@/lib/currency';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function WizardPage() {
  const step = useFarmStore((s) => s.wizardStep);
  const setStep = useFarmStore((s) => s.setStep);
  const preferences = useFarmStore((s) => s.preferences);
  const setPreferences = useFarmStore((s) => s.setPreferences);
  const setRecommendations = useFarmStore((s) => s.setRecommendations);
  const navigate = useNavigate();
  const { currency, formatFromBase } = useCurrency();
  const [photoPreview, setPhotoPreview] = useState<string | null>(preferences.spacePhoto || null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setPhotoError('Only JPG and PNG files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('File size must be under 5MB');
      return;
    }
    setPhotoError(null);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setPhotoPreview(base64);
      setPreferences({ spacePhoto: base64 });
    };
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhotoPreview(null);
    setPreferences({ spacePhoto: undefined });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleNext = async () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      let results = generateRecommendations(preferences);
      let sessionId: string | undefined;
      try {
        results = await api<FarmingPackage[]>('/api/recommendations', {
          method: 'POST',
          body: JSON.stringify(preferences),
        });
        const session = await api<{ id: string }>('/api/recommendations/session', {
          method: 'POST',
          body: JSON.stringify({
            preferences,
            packages: results
          }),
        });
        sessionId = session?.id;
      } catch {
        // Fallback to local recommendation generation if API is unavailable.
      }
      setRecommendations(results, sessionId);
      navigate('/results');
    }
  };
  const handleBack = () => {
    setStep(step - 1);
  };
  const steps = [
    {
      title: "What do you want to farm?",
      description: "Pick the livestock you're planning to raise.",
      options: [
        { id: 'poultry', label: 'Poultry', description: 'Chickens, turkeys, ducks', icon: <Bird className="h-4 w-4" /> },
        { id: 'fish', label: 'Fish', description: 'Catfish and tilapia', icon: <Fish className="h-4 w-4" /> },
        { id: 'cattle', label: 'Cattle', description: 'Dairy or beef', icon: <Trees className="h-4 w-4" /> },
        { id: 'goats', label: 'Goats', description: 'Meat or milk goats', icon: <UserCircle className="h-4 w-4" /> },
        { id: 'rabbits', label: 'Rabbits', description: 'Meat rabbits', icon: <Rabbit className="h-4 w-4" /> },
      ],
      field: 'livestockType'
    },
    {
      title: "What's your farming experience?",
      description: "This helps us recommend the right setup for you.",
      options: [
        { id: 'beginner', label: 'First timer', description: 'Never farmed before', icon: <UserCircle className="h-4 w-4" /> },
        { id: 'intermediate', label: 'Some experience', description: 'Raised animals before', icon: <UserCircle className="h-4 w-4" /> },
        { id: 'expert', label: 'Expert', description: 'Running a commercial farm', icon: <UserCircle className="h-4 w-4" /> },
      ],
      field: 'experience'
    }
  ];
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-16">
        <div className="mb-12">
          <div className="flex justify-between mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={cn(
                  "h-1.5 flex-1 mx-1 rounded-full transition-colors",
                  step + 1 >= i ? 'bg-primary' : 'bg-muted-foreground/20'
                )}
              />
            ))}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Step {step + 1} of 5</div>
        </div>
        {step < 2 ? (
          <WizardStep
            title={steps[step].title}
            description={steps[step].description}
            options={steps[step].options}
            currentValue={preferences[steps[step].field as keyof typeof preferences] as string}
            onSelect={(id) => setPreferences({ [steps[step].field]: id })}
            onNext={handleNext}
            onBack={handleBack}
            isFirst={step === 0}
          />
        ) : step === 2 ? (
          <div className="space-y-6">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold tracking-tight">Budget and space</h2>
              <p className="text-sm text-muted-foreground">How much can you spend, and how much space do you have?</p>
            </div>
            <div className="grid gap-6 py-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                    <DollarSign className="h-3 w-3" /> Total budget
                  </Label>
                  <span className="text-sm font-bold text-primary">{formatFromBase(preferences.budget)}</span>
                </div>
                <Slider
                  value={[preferences.budget]}
                  min={500}
                  max={50000}
                  step={500}
                  onValueChange={([val]) => setPreferences({ budget: val })}
                  className="py-2"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{formatFromBase(500)}</span>
                  <span>{formatFromBase(50000)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                  <Square className="h-3 w-3" /> Available space (m²)
                </Label>
                <Input
                  id="space"
                  type="number"
                  className="rounded-md border-border"
                  value={preferences.spaceSqM}
                  onChange={(e) => setPreferences({ spaceSqM: Number(e.target.value) })}
                  placeholder="e.g., 50"
                />
              </div>
              <div className="p-3 bg-muted/30 rounded-md text-xs text-muted-foreground">
                <p>💡 For {preferences.livestockType === 'fish' ? 'pond' : 'pen'} space: 1m² accommodates approximately {
                  preferences.livestockType === 'catfish' ? '150-200 fish' :
                  preferences.livestockType === 'tilapia' ? '100-150 fish' :
                  preferences.livestockType === 'poultry' ? '10-15 birds' :
                  preferences.livestockType === 'rabbits' ? '4-6 rabbits' : 'varies'
                }</p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-6 border-t">
              <Button variant="outline" size="sm" onClick={handleBack}>Previous</Button>
              <Button size="sm" onClick={handleNext}>Continue</Button>
            </div>
          </div>
        ) : step === 3 ? (
          <div className="space-y-6">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold tracking-tight">Where will you farm?</h2>
              <p className="text-sm text-muted-foreground">Location affects delivery cost and return estimates.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal" className="text-xs font-bold uppercase text-muted-foreground">Main goal</Label>
              <select
                id="goal"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={preferences.goal}
                onChange={(e) => setPreferences({ goal: e.target.value as typeof preferences.goal })}
              >
                <option value="family-consumption">Family consumption</option>
                <option value="side-income">Side income</option>
                <option value="primary-income">Primary income</option>
                <option value="commercial">Commercial scale</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase text-muted-foreground">Country</Label>
              <Select value={preferences.country || 'GH'} onValueChange={(val) => setPreferences({ country: val, region: undefined })}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code} className="text-xs">
                      {c.flag} {c.name} ({c.currency})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal" className="text-xs font-bold uppercase text-muted-foreground">Main goal</Label>
              <select
                id="goal"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={preferences.goal}
                onChange={(e) => setPreferences({ goal: e.target.value as typeof preferences.goal })}
              >
                <option value="family-consumption">Family consumption</option>
                <option value="side-income">Side income</option>
                <option value="primary-income">Primary income</option>
                <option value="commercial">Commercial scale</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase text-muted-foreground">Location type</Label>
              <div className="grid sm:grid-cols-3 gap-2">
                {['Urban', 'Suburban', 'Rural'].map((loc) => (
                  <button
                    key={loc}
                    onClick={() => setPreferences({ location: loc })}
                    className={cn(
                      "text-left p-3 rounded-md border transition-colors",
                      preferences.location === loc
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "bg-background border-border hover:bg-muted"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className={cn("h-4 w-4", preferences.location === loc ? "text-primary" : "text-muted-foreground")} />
                      <div className="text-sm font-bold">{loc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                <Landmark className="h-3 w-3" /> Region / State
              </Label>
              <Select 
                value={preferences.region || ''} 
                onValueChange={(val) => val && setPreferences({ region: val })}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder={`Select ${preferences.country === 'NG' ? 'state' : 'region'}`} />
                </SelectTrigger>
                <SelectContent>
                  {(preferences.country === 'NG' ? NG_STATES : GH_REGIONS).map((r) => (
                    <SelectItem key={r} value={r} className="text-xs">
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="landmark" className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                <Landmark className="h-3 w-3" /> Nearest landmark
              </Label>
              <Input
                id="landmark"
                type="text"
                className="rounded-md border-border"
                value={preferences.nearestLandmark || ''}
                onChange={(e) => setPreferences({ nearestLandmark: e.target.value })}
                placeholder="e.g., Tema Market, Lagos Airport"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 py-2">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                  <Zap className="h-3 w-3" /> Has electricity
                </Label>
                <div className="flex gap-2">
                  {[
                    { id: 'yes', label: 'Yes' },
                    { id: 'no', label: 'No' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setPreferences({ hasElectricity: opt.id === 'yes' })}
                      className={cn(
                        "flex-1 py-2 rounded-md border text-sm transition-colors",
                        preferences.hasElectricity === (opt.id === 'yes')
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "bg-background border-border hover:bg-muted"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                  <Droplets className="h-3 w-3" /> Has water source
                </Label>
                <div className="flex gap-2">
                  {[
                    { id: 'yes', label: 'Yes' },
                    { id: 'no', label: 'No' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setPreferences({ hasWaterSource: opt.id === 'yes' })}
                      className={cn(
                        "flex-1 py-2 rounded-md border text-sm transition-colors",
                        preferences.hasWaterSource === (opt.id === 'yes')
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "bg-background border-border hover:bg-muted"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-2 pt-2">
              <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                <Camera className="h-3 w-3" /> Space photo (optional)
              </Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handlePhotoUpload}
                className="w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              {photoError && (
                <p className="text-xs text-red-500">{photoError}</p>
              )}
              {photoPreview && (
                <div className="relative mt-2">
                  <img 
                    src={photoPreview} 
                    alt="Space preview" 
                    className="w-full max-h-48 object-cover rounded-md border"
                  />
                  <button
                    onClick={clearPhoto}
                    className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="time" className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                <Clock className="h-3 w-3" /> Time availability
              </Label>
              <Select value={preferences.timeAvailability} onValueChange={(val) => setPreferences({ timeAvailability: val as any })}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekends-only">Weekends only - I have limited time</SelectItem>
                  <SelectItem value="daily-attention">Daily attention - Check regularly</SelectItem>
                  <SelectItem value="full-time">Full-time - I'm committed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {preferences.livestockType === 'fish' && (
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2">
                  <Droplets className="h-3 w-3" /> Water source
                </Label>
                <Select value={preferences.waterSource || 'borehole'} onValueChange={(val) => setPreferences({ waterSource: val as any })}>
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="borehole">Borehole</SelectItem>
                    <SelectItem value="municipal">Municipal supply</SelectItem>
                    <SelectItem value="river-stream">River/stream</SelectItem>
                    <SelectItem value="rain-harvest">Rain harvest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-between items-center pt-6 border-t">
              <Button variant="outline" size="sm" onClick={handleBack}>Previous</Button>
              <Button size="sm" onClick={handleNext}>Continue</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-1">
              <h2 className="text-2xl font-bold tracking-tight">Do you need training?</h2>
              <p className="text-sm text-muted-foreground">This kit helps you avoid common mistakes first-time farmers make.</p>
            </div>
            <div
              className={cn(
                "p-6 border rounded-md transition-all cursor-pointer",
                preferences.includeTrainingUpsell ? "border-primary bg-primary/5" : "bg-card hover:border-primary/50"
              )}
              onClick={(e) => {
                // Prevent toggle if clicking interactive elements inside
                const target = e.target as HTMLElement;
                if (target.closest('button') || target.closest('input')) return;
                setPreferences({ includeTrainingUpsell: !preferences.includeTrainingUpsell });
              }}
            >
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded text-primary">
                  <GraduationCap className="h-6 w-6" />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-base">Startup training kit</h3>
                    <div className="text-sm font-bold text-primary">+{formatFromBase(45)}</div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    PDF setup guides, 3 months of text support, and a 12-month feeding and vaccination schedule for your animals.
                  </p>
                  <div className="flex items-center gap-2 pt-2">
                    <Checkbox 
                      id="training-checkbox"
                      checked={preferences.includeTrainingUpsell} 
                      onCheckedChange={(val) => setPreferences({ includeTrainingUpsell: !!val })} 
                    />
                    <Label htmlFor="training-checkbox" className="text-xs font-bold cursor-pointer">
                      Add to my recommendation package
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-muted/30 p-4 rounded-md flex items-start gap-3 border">
              <ShieldCheck className="h-4 w-4 text-primary mt-0.5" />
              <p className="text-[10px] text-muted-foreground leading-normal italic">
                Recommended for {preferences.experience} farmers. Most successful customers use this.
              </p>
            </div>
            <div className="flex justify-between items-center pt-6 border-t">
              <Button variant="outline" size="sm" onClick={handleBack}>Previous</Button>
              <Button size="sm" onClick={handleNext} className="bg-primary hover:bg-primary/90">
                Show my recommendations
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}